import { useEffect, useState } from "react";
import { Tablet, Plus, Loader2, AlertCircle, Copy, CheckCircle2, ShieldOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, auth as authApi, devices as devicesApi, type DeviceRow } from "@/lib/api";

export default function Devices() {
  const [rows, setRows] = useState<DeviceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [provisionOpen, setProvisionOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newSite, setNewSite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [provisioned, setProvisioned] = useState<{ device_id: string; pairing_code: string } | null>(null);
  const [copied, setCopied] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setRows(await devicesApi.list());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to load devices");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleProvision(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await authApi.provisionDevice({
        label: newLabel.trim(),
        site_id: newSite.trim() || undefined,
      });
      setProvisioned(result);
      setNewLabel("");
      setNewSite("");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Provisioning failed");
    } finally {
      setSubmitting(false);
    }
  }

  function copyCode() {
    if (!provisioned) return;
    void navigator.clipboard.writeText(provisioned.pairing_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function handleRevoke(id: string) {
    if (!confirm("Revoke this device? It will be signed out immediately.")) return;
    try {
      await devicesApi.revoke(id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Revoke failed");
    }
  }

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Tablet className="h-6 w-6" /> Devices
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tablets paired to this organisation. Operators sign in by typing their name on a paired tablet.
          </p>
        </div>
        <Button onClick={() => { setProvisionOpen(true); setProvisioned(null); }}>
          <Plus className="h-4 w-4 mr-1" /> Pair new tablet
        </Button>
      </div>

      {error && (
        <Card className="mb-4 border-destructive bg-destructive/10">
          <CardContent className="p-3 flex items-center gap-2 text-sm">
            <AlertCircle className="h-4 w-4 text-destructive" /> {error}
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center text-muted-foreground flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading devices...
          </CardContent>
        </Card>
      )}

      {!loading && rows.length === 0 && (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center text-muted-foreground">
            No tablets paired yet. Click <strong>Pair new tablet</strong> above to generate a code.
          </CardContent>
        </Card>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-3">
          {rows.map((d) => (
            <Card key={d.id} className={d.revoked ? "border-destructive/40 bg-destructive/5" : "bg-card border-border"}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">{d.label}</span>
                      {d.revoked && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-destructive/20 text-destructive font-medium">revoked</span>}
                      {d.pairing_code && !d.revoked && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-warning/20 text-warning font-medium">pending pair</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {d.site_id ? <>Site: <span className="text-foreground">{d.site_id}</span> · </> : null}
                      Created {new Date(d.created_at).toLocaleString()}
                      {d.last_seen_at && <> · Last seen {new Date(d.last_seen_at).toLocaleString()}</>}
                    </p>
                    {d.pairing_code && !d.revoked && (
                      <p className="text-xs mt-2">
                        <span className="text-muted-foreground">Pairing code: </span>
                        <span className="font-mono text-sm font-bold text-foreground">{d.pairing_code}</span>
                      </p>
                    )}
                  </div>
                  {!d.revoked && (
                    <Button variant="outline" size="sm" onClick={() => void handleRevoke(d.id)}>
                      <ShieldOff className="h-3.5 w-3.5 mr-1" /> Revoke
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={provisionOpen} onOpenChange={(o) => { setProvisionOpen(o); if (!o) setProvisioned(null); }}>
        <DialogContent>
          {!provisioned ? (
            <form onSubmit={(e) => void handleProvision(e)}>
              <DialogHeader>
                <DialogTitle>Pair a new tablet</DialogTitle>
                <DialogDescription>
                  Give this tablet a label so you can recognise it later. You'll get a one-time pairing code to type into the tablet.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 my-4">
                <div className="space-y-1.5">
                  <Label htmlFor="label">Tablet label</Label>
                  <Input
                    id="label"
                    placeholder="e.g. D65-Tablet-3"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="site">Site (optional)</Label>
                  <Input
                    id="site"
                    placeholder="e.g. Wodgina"
                    value={newSite}
                    onChange={(e) => setNewSite(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setProvisionOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting || !newLabel.trim()}>
                  {submitting ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generating...</> : "Generate code"}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Tablet paired — share the code</DialogTitle>
                <DialogDescription>
                  Open the operator app on the tablet and paste this code in the "Pair this tablet" field. The code only works once.
                </DialogDescription>
              </DialogHeader>
              <div className="my-6">
                <div className="flex items-center justify-center bg-muted/50 rounded-lg py-6">
                  <span className="font-mono text-3xl tracking-wider font-bold">{provisioned.pairing_code}</span>
                </div>
                <Button type="button" className="w-full mt-3" onClick={copyCode}>
                  {copied ? <><CheckCircle2 className="h-5 w-5 mr-2" /> Copied!</> : <><Copy className="h-5 w-5 mr-2" /> Copy to clipboard</>}
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={() => setProvisionOpen(false)}>Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
